'use strict';
// public/v3/src/components/order-doc/test-alternatives.cjs — O4 (PR B).
//
// The pure half of the lab card's test-name combobox: everything a keystroke or
// a click DECIDES lives here, so it is unit-testable (the repo has no DOM
// harness — jest testEnvironment:'node', no jsdom). The .jsx sibling
// (lab-order-card.jsx TestNameCombobox) only renders these descriptors and
// fires the edit they name; order-doc-helpers.cjs is the same pattern.
//
// Input is one `perTestResolution` row exactly as PR #665's DTO emits it:
//   { name, requested, code, description, codes:[{code,description}],
//     status, confidence, source, candidates[], needsResolution,
//     testId, testCode, alternatives:[{ id, name, testCode, why }] }
//
// TWO CODE NAMESPACES, never mixed: `testCode` is the VENDOR code (LabCorp
// `001925`) that identifies the assay to the provider; `code`/`codes` are
// ICD-10 indications. `testId` is the Elation catalog id a swap addresses.
//
// Why the vendor code is not decoration: prod carries 122 live
// same-name/different-code groups (342 rows) — four distinct
// `Buprenorphine, Urine` assays among them. Resolution hides them by design
// (owner ruling O10: order the freshest row under a name); this list is what
// makes them reachable, and inside such a group the code is the ONLY thing
// telling two options apart. So every option carries a renderable code.
//
// O24 adds a SECOND line under each option — `meta`, e.g.
// "CPT 82306 · added 2017 · asks 2 questions" — composed by the shared
// lab-choice-meta module, which the picker chips and the SMS list use too, so
// the three surfaces cannot describe one row three ways. '' when the row says
// nothing, which is how the .jsx knows not to draw the line.
const { choiceMetaLine } = require('../../lib/lab-choice-meta.cjs');

// `why` (engine enum) → the one word the card shows. A name-derived reason says
// what the swap would CHANGE (reflex/panel/variant), which is what the provider
// is choosing between; `offered`/`previous` say where the row came from.
const REASON_LABELS = {
  reflex: 'reflex',
  panel: 'panel',
  variant: 'variant',
  plain: 'closest',      // "the plainest scoring row" is an engine fact; this is what it MEANS
  similar: 'similar name',
  offered: 'offered',
  previous: 'previous',
};

// An unknown / absent reason renders NOTHING. A future `why` this card has not
// been taught must not be guessed at beside a clinical choice, and a raw enum
// value is not English.
function altReasonLabel(why) {
  return REASON_LABELS[why] || '';
}

function _str(v) {
  return v == null ? null : String(v);
}

// The listbox contents: option 0 is the row the order carries now (offered as
// "keep", so a stray Enter changes nothing), then the compendium rows the
// matcher actually scored — IN THE MATCHER'S OWN ORDER, never re-sorted. That
// order is `cls` desc then `score` desc: exactly what a picker would have shown,
// so the list and the picker cannot disagree.
//
// Returns [] — no list at all, the name opens today's plain rename input — when:
//   * the line had no genuine choice (`alternatives` empty or absent): a lone
//     survivor, an order-set row, a practice-created row, a quest row, or a
//     MERGED line (S-4: "considered" is ill-defined across two dictations);
//   * the line has no `testId`: a rename miss (`needsResolution`) has no
//     identity, so a swap would have no `fromTestId` to send and the engine
//     would 400. Never offer what cannot be committed.
function alternativeOptions(row) {
  const fromTestId = _str(row && row.testId);
  const alts = (row && Array.isArray(row.alternatives)) ? row.alternatives : [];
  if (!fromTestId || !alts.length) return [];
  const name = (row && row.name) || null;
  const currentCode = _str(row.testCode);
  const options = [{
    index: 0,
    current: true,
    id: fromTestId,
    name: name || '(unnamed test)',
    label: name ? `Keep ${name}` : 'Keep this test',
    code: currentCode,
    codeText: currentCode || '—',
    // The row the line carries now describes itself too: it is option 0 of a
    // comparison, and comparing it against alternatives that state their CPT
    // and generation while it states nothing would be the wrong way round.
    // (The CLOSED row stays code-only — O22/E-9: it is not a choice.)
    meta: choiceMetaLine(row),
    reason: 'current',
  }];
  for (const a of alts) {
    const id = _str(a && a.id);
    // No id → nothing to address. The line's own id → that IS option 0 (the
    // engine excludes the chosen row, so this is belt-and-braces).
    if (!id || id === fromTestId) continue;
    const code = _str(a.testCode);
    const altName = a.name || '(unnamed test)';
    options.push({
      index: options.length,
      current: false,
      id,
      name: altName,
      label: altName,
      code,
      codeText: code || '—',
      meta: choiceMetaLine(a),
      reason: altReasonLabel(a.why),
    });
  }
  return options;
}

// The quiet affordance beside the name: "▾ 3 more" (the caret is the .jsx's,
// aria-hidden). Null when there is nothing to show — O4's whole point is that a
// provider is never FORCED to interact with a picker, so a line with no genuine
// choice grows no chip.
//
// `count` counts the ALTERNATIVES, not the keep-current option: the chip
// promises how many other assays the matcher considered.
function moreLabel(row) {
  const options = alternativeOptions(row);
  if (options.length < 2) return null;
  const count = options.length - 1;
  const noun = count === 1 ? 'alternative' : 'alternatives';
  const name = (row && row.name) || 'this test';
  return {
    count,
    text: `${count} more`,
    ariaLabel: `${count} ${noun} for ${name}`,
    hint: `${count} ${noun}. Up and Down to choose, Enter to select, Escape to cancel.`,
  };
}

// APG listbox roving: which option becomes active, or null when the key is not
// the listbox's. Null is load-bearing — the widget only preventDefault()s a
// non-null result, so Escape still closes, Enter still commits, and with no list
// open the arrows/Home/End stay the text field's caret keys.
//
// -1 is the "typing" state (the provider has edited the text, so no option is
// selected): Down enters the list at the top, Up at the bottom.
function nextActiveIndex(key, current, count) {
  const n = Number.isInteger(count) ? count : 0;
  if (n <= 0) return null;
  const cur = (Number.isInteger(current) && current >= 0 && current < n) ? current : -1;
  if (key === 'ArrowDown') return cur < 0 ? 0 : (cur + 1) % n;
  if (key === 'ArrowUp') return cur < 0 ? n - 1 : (cur - 1 + n) % n;
  if (key === 'Home') return 0;
  if (key === 'End') return n - 1;
  return null;
}

// What a commit gesture MEANS. `reason`:
//   'enter'  — Enter in the text field
//   'select' — a click / tap on an option
//   'escape' — Escape (revert, no request, ever)
//   'blur'   — Tab or focus loss
//
// Returns one of:
//   { kind:'pick',   from, to, edit:{action:'pick-test-alternative', payload:{testIndex,testId,fromTestId}} }
//   { kind:'rename', name,     edit:{action:'set-test-name',         payload:{testIndex,name}} }
//   { kind:'keep' }   the current row was chosen — close, send nothing
//   { kind:'close' }  nothing to do — close, send nothing
//
// `fromTestId` is MANDATORY on a pick (PR #665 C1): it is the id the card
// rendered this line from, and the engine 400s when it is absent or is no longer
// the line's own id — that is how a card whose indices shifted underneath it
// cannot swap an assay nobody touched. So a pick is emitted only when both ids
// are present; otherwise the gesture closes.
//
// blur NEVER picks. Tabbing out of an open list must not order a different
// assay; only a deliberate Enter or click does. A changed text field on blur
// still renames — that is today's behaviour and it is unchanged.
function commitFor({ reason, row, testIndex, activeIndex, text } = {}) {
  const name = (row && row.name) || '';
  const typed = String(text == null ? '' : text).trim();
  const renameOrClose = () => (
    (typed && typed !== name)
      ? { kind: 'rename', name: typed, edit: { action: 'set-test-name', payload: { testIndex, name: typed } } }
      : { kind: 'close' }
  );

  if (reason === 'escape') return { kind: 'close' };
  if (reason === 'blur') return renameOrClose();
  if (reason !== 'enter' && reason !== 'select') return { kind: 'close' };

  const options = alternativeOptions(row);
  const idx = Number.isInteger(activeIndex) ? activeIndex : -1;
  if (idx >= 0 && idx < options.length) {
    const opt = options[idx];
    if (opt.current) return { kind: 'keep' };
    const fromTestId = _str(row && row.testId);
    if (!opt.id || !fromTestId) return { kind: 'close' };
    return {
      kind: 'pick',
      from: { name: name || null, code: _str(row.testCode) },
      to: { name: opt.name, code: opt.code },
      edit: {
        action: 'pick-test-alternative',
        payload: { testIndex, testId: opt.id, fromTestId },
      },
    };
  }
  // idx === -1 (typing) or out of range: the text field is what was committed.
  return renameOrClose();
}

// What the card's aria-live region says. Accepts a commitFor() descriptor
// directly — so the sentence and the request cannot drift — plus the two
// outcomes only the response knows:
//   { kind:'unresolved' }        a rename matched no catalog row
//   { kind:'error', message }    the route refused; `message` is the server's
//                                own sentence, spoken verbatim because it is
//                                what the provider must act on.
// A same-name swap is only distinguishable by its code, so the code is spoken.
function swapAnnouncement(result) {
  if (!result) return '';
  if (result.kind === 'pick') {
    const to = result.to || {};
    const from = result.from || {};
    const code = to.code ? ` (${to.code})` : '';
    return `Ordering ${to.name || 'that test'}${code} instead of ${from.name || 'the previous test'}`;
  }
  if (result.kind === 'rename') return `Renamed to ${result.name}`;
  if (result.kind === 'unresolved') return "Couldn't match that test — rename or remove it";
  if (result.kind === 'error') {
    return String(result.message == null ? '' : result.message).trim() || "Couldn't apply that change";
  }
  return '';
}

// What the field says under itself, and whether the provider is TYPING — one
// decision, because they are the same one: if Enter would rename, the sentence
// has to say so, and the component shows it visibly instead of only to a screen
// reader. `active >= 0` means an option is selected, so Enter would PICK — the
// hint must not promise a rename then, however much text has been edited.
// A plain row (no list) never says "pick below instead": there is nothing below
// it, and an instruction the provider cannot follow is worse than none.
function hintFor({ text, name, active, listOpen, more } = {}) {
  const typed = String(text == null ? '' : text).trim();
  const selecting = Number.isInteger(active) && active >= 0;
  const typing = !selecting && !!typed && typed !== ((name == null ? '' : String(name)));
  if (typing) {
    return {
      typing: true,
      text: listOpen ? `Enter renames to “${typed}”; pick below instead` : `Enter renames to “${typed}”`,
    };
  }
  if (listOpen && more && more.hint) return { typing: false, text: more.hint };
  return { typing: false, text: 'Type to rename. Enter to save, Escape to cancel.' };
}

// Which lines just became unmatchable, by NAME. A `needsResolution` row carries
// no catalog identity, so it has no id to key on; keying on the COUNT missed a
// miss renamed to another miss (zzqqx → zzqqy left the count equal and the
// provider heard only "Renamed to zzqqy"), and keying on the INDEX would
// re-announce a surviving miss whenever a row above it was removed.
// Duplicate names collapse — two identical misses are one fact about the order.
function _unresolvedNames(rows) {
  const out = [];
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (r && r.needsResolution) out.push(r.name || '(unnamed test)');
  }
  return out;
}
function gainedUnresolved(prevRows, rows) {
  const before = new Set(_unresolvedNames(prevRows));
  const seen = new Set();
  return _unresolvedNames(rows).filter((n) => {
    if (before.has(n) || seen.has(n)) return false;
    seen.add(n);
    return true;
  });
}

// OF-659-1: every dictated indication now rides its line, so a line can carry
// more than one ICD-10. `codes[0]` IS the row's `code`/`description` (the
// confidence chip describes the primary), so the extras are literally
// `codes.slice(1)` — rendered as small chips after the primary. A malformed
// entry is dropped rather than drawn as a blank chip.
function extraCodes(row) {
  const codes = (row && Array.isArray(row.codes)) ? row.codes : [];
  return codes.slice(1)
    .filter((c) => c && c.code)
    .map((c) => ({ code: String(c.code), description: c.description || null }));
}

module.exports = {
  altReasonLabel,
  alternativeOptions,
  moreLabel,
  nextActiveIndex,
  commitFor,
  swapAnnouncement,
  extraCodes,
  hintFor,
  gainedUnresolved,
};
